import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, X, ChevronRight, Gavel, Settings2 } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  pending: "bg-champagne/10 text-champagne border-champagne/30",
  approved: "bg-[#4D5B46]/30 text-[#A3C293] border-[#4D5B46]/50",
  rejected: "bg-[#5C2B29]/30 text-[#E5A09E] border-[#5C2B29]/50",
  cancelled: "bg-elevated text-textmuted border-seam",
  escalated: "bg-[#7a4a30]/20 text-[#E5B493] border-[#7a4a30]/50",
};

const Approvals = () => {
  const { hasPermission } = useAuth();
  const canDecide = hasPermission("approvals.approve");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals", { params: filter ? { status: filter } : {} });
      setItems(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load approvals");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (item, decision) => {
    const note = window.prompt(`Optional note for ${decision}:`, "");
    try {
      await api.post(`/approvals/${item.id}/decision`, { decision, note: note || "" });
      toast.success(`${decision === "approve" ? "Approved" : decision === "reject" ? "Rejected" : "Escalated"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  return (
    <Layout>
      <section className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow">Governance</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Approval Center</h1>
          <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
            Every request awaiting executive judgment — expenses, purchases, contracts, publishing,
            pricing changes and operational exceptions.
          </p>
        </div>
        {hasPermission("admins.manage") && (
          <Link
            to="/approvals/policies"
            data-testid="manage-chains-link"
            className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-seam text-textmuted hover:text-champagne hover:border-champagne/40 mt-2"
          >
            <Settings2 className="w-3.5 h-3.5" strokeWidth={1.6} />
            Manage Approval Chains
          </Link>
        )}
      </section>

      <div className="flex gap-2 mb-5 flex-wrap">
        {["", "pending", "approved", "rejected", "escalated"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`approval-filter-${s || "all"}`}
            className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ${
              filter === s ? "bg-champagne/10 text-champagne border-champagne/40" : "border-seam text-textmuted hover:text-textmain"
            }`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-champagne animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="regal-card p-12 text-center">
          <Gavel className="w-8 h-8 text-champagne mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-textmuted">Nothing to decide.</p>
        </div>
      ) : (
        <div className="regal-card overflow-hidden">
          <div className="divide-y divide-seam">
            {items.map((a) => (
              <div key={a.id} data-testid={`approval-row-${a.id}`} className="px-5 py-4 hover:bg-elevated/40">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[a.status] || STATUS_STYLES.pending}`}>
                        {a.status}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-champagne">{a.type.replace(/_/g, " ")}</span>
                      {a.amount != null && (
                        <span className="text-[11px] text-textmain font-mono">
                          {a.currency || "USD"} {a.amount.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-xl text-textmain mt-1">{a.title}</h3>
                    {a.description && (
                      <p className="text-sm text-textmuted mt-1 leading-relaxed line-clamp-2">{a.description}</p>
                    )}
                    <p className="text-[11px] text-textmuted mt-2">
                      Requires: <span className="text-champagne">{a.required_role?.replace(/_/g, " ")}</span>
                      {a.decision_chain?.length > 0 && ` · ${a.decision_chain.length} decision(s) recorded`}
                    </p>
                    {a.chain?.length > 0 && (
                      <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid={`chain-progress-${a.id}`}>
                        {a.chain.map((step, i) => {
                          const isDone = step.status === "approved";
                          const isCurrent = i === (a.chain_index ?? 0) && a.status === "pending";
                          return (
                            <React.Fragment key={i}>
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${
                                  isDone
                                    ? "bg-[#4D5B46]/30 text-[#A3C293] border-[#4D5B46]/50"
                                    : isCurrent
                                    ? "bg-champagne/10 text-champagne border-champagne/40"
                                    : "border-seam text-textmuted"
                                }`}
                              >
                                {isDone && <Check className="w-2.5 h-2.5" strokeWidth={2.2} />}
                                {step.label || step.role.replace(/_/g, " ")}
                              </span>
                              {i < a.chain.length - 1 && <ChevronRight className="w-3 h-3 text-textmuted" strokeWidth={1.4} />}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {canDecide && a.status === "pending" && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => decide(a, "approve")}
                        data-testid={`approve-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft"
                      >
                        <Check className="w-3.5 h-3.5" strokeWidth={1.7} /> Approve
                      </button>
                      <button
                        onClick={() => decide(a, "reject")}
                        data-testid={`reject-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-seam text-textmuted hover:text-[#E5A09E] hover:border-[#5C2B29]/50"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={1.7} /> Reject
                      </button>
                      <button
                        onClick={() => decide(a, "escalate")}
                        data-testid={`escalate-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-seam text-textmuted hover:text-champagne"
                      >
                        <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.7} /> Escalate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Approvals;
